import React from 'react';

import { PlayCircleIcon, StopCircleIcon } from '@vlabs/icons';
import { ButtonScrimDivider, ButtonScrimGroup, Tooltip } from '@vlabs/uikit';
// Импорт IconButton тем же путём, что и внутри ButtonScrimGroup — иначе vite prebundle
// создаёт два экземпляра компонента и кнопки не клонируются
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button/IconButton.js';
import cn from 'classnames';

import { useStartStopStreamTranslation } from '@vlabs/monokit/features/start-stop-stream/i18n';

import st from './shared.module.sass';
import { getStreamStatusConfig } from './streamStatusConfig';

export type StreamStatusType = 'text' | 'text+button' | 'badge';

interface StreamStatusProps {
  /** Числовой статус luna-platform (StreamStatusValue). */
  status: number;
  /** Вид по макетам (node 8870-87618): пилюля с текстом, пилюля с кнопками, точечный бейдж */
  type?: StreamStatusType;
  onStart?: VoidFunction;
  onStop?: VoidFunction;
  testId?: string;
}

// Кнопки по макетам: In progress — стоп; Cancel/Unknown — старт (у Unknown ещё стоп);
// остальные — без кнопок. Числа — значения StreamStatusValue.
// ponytail: у Pending (0) в макете кнопок нет, но иначе ожидающий поток нельзя запустить
function getStatusActions(status: number): ('start' | 'stop')[] {
  switch (status) {
    case 0: // pending
      return ['start'];
    case 1: // in_progress
      return ['stop'];
    case 5: // stop
    case 8: // paused
      return ['start'];
    case 6: // unknown
      return ['start', 'stop'];
    default:
      return [];
  }
}

function StreamStatus({
  status,
  type = 'text',
  onStart = undefined,
  onStop = undefined,
  testId = undefined,
}: StreamStatusProps): React.JSX.Element {
  const { t } = useStartStopStreamTranslation();
  const streamConfig = getStreamStatusConfig(status);

  if (type === 'badge') {
    return (
      <Tooltip
        data-testid={testId}
        tooltipContent={streamConfig.label}
        tooltipProps={{ place: 'bottom' }}
      >
        <div className={st.Badge}>
          <div className={cn(st.BadgeInner, streamConfig.colorClass)} />
        </div>
      </Tooltip>
    );
  }

  const actions = type === 'text+button' ? getStatusActions(status) : [];

  const renderButton = (action: 'start' | 'stop'): React.JSX.Element | null => {
    const onClick = action === 'stop' ? onStop : onStart;
    if (!onClick) return null;

    return (
      <IconButton
        icon={action === 'stop' ? <StopCircleIcon /> : <PlayCircleIcon />}
        key={action}
        kind="secondary"
        onClick={onClick}
        title={action === 'stop' ? t('stop') : t('resume')}
        variant="flat"
      />
    );
  };

  return (
    <div
      className={cn(st.Pill, { [st.Pill_withActions]: actions.length > 0 })}
      data-testid={testId}
    >
      <span className={cn(st.PillText, streamConfig.textColorClass)}>
        {streamConfig.label}
      </span>
      {actions.length > 0 && (
        <ButtonScrimGroup
          className={st.Actions}
          orientation="horizontal"
          shape="round"
          size="small"
        >
          {/* Прямые дети — только IconButton/Divider (ButtonScrimGroup клонирует их по типу) */}
          {actions.flatMap((action, index) => {
            const items = [renderButton(action)];
            // Divider просто разделяет несколько экшенов — после каждого, кроме последнего
            if (index < actions.length - 1) {
              items.push(<ButtonScrimDivider key={`divider-${action}`} />);
            }
            return items;
          })}
        </ButtonScrimGroup>
      )}
    </div>
  );
}

export { StreamStatus };
